import React, { useCallback, useMemo } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ListItem,
} from '@/components/ui';
import { IconChevronDown, IconFilter } from '@tabler/icons-react';
import { FileStatus, TableMode } from '../types';

interface FilterOption {
  value: FileStatus | 'all';
  label: string;
}

interface FileTableFiltersProps {
  tableMode: TableMode;
  statusFilter: FileStatus | 'all';
  onStatusFilterChange: (filter: FileStatus | 'all') => void;
}

export const FileTableFilters: React.FC<FileTableFiltersProps> = ({
  tableMode,
  statusFilter,
  onStatusFilterChange,
}) => {
  // Get filter options based on table mode
  const getFilterOptions = useCallback((): FilterOption[] => {
    switch (tableMode) {
      case TableMode.DUPLICATE_CHECK:
        return [
          { value: 'all', label: 'All' },
          { value: 'ready-to-check', label: 'Ready to check' },
          { value: 'duplicated', label: 'Duplicated' },
        ];
      case TableMode.VALIDATION:
        return [
          { value: 'all', label: 'All' },
          { value: 'allowed', label: 'Allowed' },
          { value: 'not-allowed', label: 'Not Allowed' },
          { value: 'exists', label: 'Exists' },
        ];
      case TableMode.UPLOAD_PROGRESS:
        return [
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending Upload' },
          { value: 'uploading', label: 'Uploading' },
          { value: 'uploaded', label: 'Uploaded' },
          { value: 'failed', label: 'Failed' },
        ];
      default:
        return [{ value: 'all', label: 'All' }];
    }
  }, [tableMode]);

  // Status filter dropdown component
  const statusFilterDropdown = useMemo(() => {
    const filterOptions = getFilterOptions();
    const currentFilterLabel =
      filterOptions.find(opt => opt.value === statusFilter)?.label || 'All';

    return (
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            variant="outlined"
            size="small"
            startIcon={<IconFilter />}
            endIcon={<IconChevronDown />}
          >
            Status: {currentFilterLabel}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent variant="soft">
          {filterOptions.map(option => (
            <DropdownMenuItem key={option.value} asChild>
              <ListItem
                primary={option.label}
                onClick={() => onStatusFilterChange(option.value)}
                selected={statusFilter === option.value}
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }, [getFilterOptions, statusFilter, onStatusFilterChange]);

  return statusFilterDropdown;
};
